import { AxiosError } from 'axios';
import { ServerResponse, WithAuditDetails } from 'data/types';
import { History } from 'history';
import { Dispatch, Action } from 'redux';
import { RootState } from 'data/root.reducers';
import i18n from 'i18n';
import { showSuccessMessage, showError } from 'shared/toaster';
import { ThunkDispatch } from 'redux-thunk';
import { extractErrorMessage } from '../../utils';
import * as api from './targetDeliveryTemplateAccessFunction.api';
import { getSelectedTargetDeliveryTemplateAccessFunction } from './targetDeliveryTemplateAccessFunction.selectors';
import {
  GET_TARGET_DELIVERY_TEMPLATE_ACCESS_FUNCTION_LIST,
  GET_TARGET_DELIVERY_TEMPLATE_ACCESS_FUNCTION,
  CREATE_TARGET_DELIVERY_TEMPLATE_ACCESS_FUNCTION,
  UPDATE_TARGET_DELIVERY_TEMPLATE_ACCESS_FUNCTION,
  DELETE_TARGET_DELIVERY_TEMPLATE_ACCESS_FUNCTION,
  GO_TO_TARGET_DELIVERY_TEMPLATE_ACCESS_FUNCTION_LIST,
  GO_TO_TARGET_DELIVERY_TEMPLATE_ACCESS_FUNCTION_EDIT,
  GO_TO_TARGET_DELIVERY_TEMPLATE_ACCESS_FUNCTION_VIEW,
  GO_TO_TARGET_DELIVERY_TEMPLATE_ACCESS_FUNCTION_CREATE,
  SELECT_TARGET_DELIVERY_TEMPLATE_ACCESS_FUNCTION,
  DeleteTargetDeliveryTemplateAccessFunctionThunkAction,
  UpdateTargetDeliveryTemplateAccessFunctionThunkAction,
  CreateTargetDeliveryTemplateAccessFunctionThunkAction,
  SET_TARGET_DELIVERY_TEMPLATE_ACCESS_FUNCTION_LOADING,
  SetTargetDeliveryTemplateAccessFunctionLoadingAction,
  TargetDeliveryTemplateAccessFunctionActionTypes,
  TargetDeliveryTemplateAccessFunction,
  SelectTargetDeliveryTemplateAccessFunctionAction,
} from './targetDeliveryTemplateAccessFunction.types';
import { SYSTEM_CONFIGURATION_ROUTE, TARGET_DELIVERY_TEMPLATE_PER_ACCESS_FUNCTION, TARGET_TEMPLATE } from '../../constants';

export function goToTargetDeliveryTemplateAccessFunctionList(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_TARGET_DELIVERY_TEMPLATE_ACCESS_FUNCTION_LIST,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${TARGET_TEMPLATE}${TARGET_DELIVERY_TEMPLATE_PER_ACCESS_FUNCTION}`);
  };
}

export function goToTargetDeliveryTemplateAccessFunctionCreate(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_TARGET_DELIVERY_TEMPLATE_ACCESS_FUNCTION_CREATE,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${TARGET_TEMPLATE}${TARGET_DELIVERY_TEMPLATE_PER_ACCESS_FUNCTION}/create`);
  };
}

export function goToTargetDeliveryTemplateAccessFunctionEdit(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_TARGET_DELIVERY_TEMPLATE_ACCESS_FUNCTION_EDIT,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${TARGET_TEMPLATE}${TARGET_DELIVERY_TEMPLATE_PER_ACCESS_FUNCTION}/edit`);
  };
}

export function goToTargetDeliveryTemplateAccessFunctionView(history: History): (dispatch: Dispatch) => void {
  return (dispatch: Dispatch) => {
    dispatch({
      type: GO_TO_TARGET_DELIVERY_TEMPLATE_ACCESS_FUNCTION_VIEW,
    });

    history.push(`${SYSTEM_CONFIGURATION_ROUTE}${TARGET_TEMPLATE}${TARGET_DELIVERY_TEMPLATE_PER_ACCESS_FUNCTION}/view`);
  };
}

export function setTargetDeliveryTemplateAccessFunctionLoadingAction(
  isLoading: boolean,
): SetTargetDeliveryTemplateAccessFunctionLoadingAction {
  return {
    type: SET_TARGET_DELIVERY_TEMPLATE_ACCESS_FUNCTION_LOADING,
    payload: { isLoading },
  };
}

export function getTargetDeliveryTemplateAccessFunctionList(
  deliveryFunctionId: string,
): TargetDeliveryTemplateAccessFunctionActionTypes {
  return {
    type: GET_TARGET_DELIVERY_TEMPLATE_ACCESS_FUNCTION_LIST,
    payload: api.getTargetDeliveryTemplateAccessFunctionList(deliveryFunctionId),
  };
}

export function getTargetDeliveryTemplateAccessFunction(
  deliveryFunctionId: string, id: string
): TargetDeliveryTemplateAccessFunctionActionTypes {
  return {
    type: GET_TARGET_DELIVERY_TEMPLATE_ACCESS_FUNCTION,
    payload: api.getTargetDeliveryTemplateAccessFunction(deliveryFunctionId, id),
  };
}

export function createTargetDeliveryTemplateAccessFunction(
  deliveryFunctionId: string,
  body: WithAuditDetails<TargetDeliveryTemplateAccessFunction>,
  history: History,
): CreateTargetDeliveryTemplateAccessFunctionThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>) => {
    try {
      await dispatch({
        type: CREATE_TARGET_DELIVERY_TEMPLATE_ACCESS_FUNCTION,
        payload: api.createTargetDeliveryTemplateAccessFunction(deliveryFunctionId, body),
      });

      dispatch(getTargetDeliveryTemplateAccessFunctionList(deliveryFunctionId));
      dispatch(goToTargetDeliveryTemplateAccessFunctionList(history));
      showSuccessMessage(i18n.t('targetDeliveryTemplateAccessFunction.create.success', { name: body.data.name }), 5000);

    } catch (e) {
      const errorMessage = extractErrorMessage(e as AxiosError<ServerResponse<void>>);
      showError(i18n.t('targetDeliveryTemplateAccessFunction.create.failed', { name: body.data.name, error: errorMessage }));
    }
  };
}

export function createTargetDeliveryTemplateAccessFunctionFromForm(
  deliveryFunctionId: string,
  body: WithAuditDetails<TargetDeliveryTemplateAccessFunction>,
): CreateTargetDeliveryTemplateAccessFunctionThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>) => {
    dispatch(setTargetDeliveryTemplateAccessFunctionLoadingAction(true));
    try {
      await dispatch({
        type: CREATE_TARGET_DELIVERY_TEMPLATE_ACCESS_FUNCTION,
        payload: api.createTargetDeliveryTemplateAccessFunction(deliveryFunctionId, body),
      });

      showSuccessMessage(i18n.t('targetDeliveryTemplateAccessFunction.create.success', { name: body.data.name }), 5000);

    } catch (e) {
      const errorMessage = extractErrorMessage(e as AxiosError<ServerResponse<void>>);
      showError(i18n.t('targetDeliveryTemplateAccessFunction.create.failed', { name: body.data.name, error: errorMessage }));

    } finally {
      dispatch(setTargetDeliveryTemplateAccessFunctionLoadingAction(false));
      dispatch(getTargetDeliveryTemplateAccessFunctionList(deliveryFunctionId));
    }
  };
}

export function updateTargetDeliveryTemplateAccessFunction(
  deliveryFunctionId: string,
  id: string,
  body: WithAuditDetails<TargetDeliveryTemplateAccessFunction>,
  history: History,
): UpdateTargetDeliveryTemplateAccessFunctionThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>) => {
    try {
      await dispatch({
        type: UPDATE_TARGET_DELIVERY_TEMPLATE_ACCESS_FUNCTION,
        payload: api.updateTargetDeliveryTemplateAccessFunction(deliveryFunctionId, id, body),
      });

      dispatch(getTargetDeliveryTemplateAccessFunctionList(deliveryFunctionId));
      dispatch(goToTargetDeliveryTemplateAccessFunctionList(history));
      showSuccessMessage(i18n.t('targetDeliveryTemplateAccessFunction.update.success', { name: body.data.name }), 5000);

    } catch (e) {
      const errorMessage = extractErrorMessage(e as AxiosError<ServerResponse<void>>);
      showError(i18n.t('targetDeliveryTemplateAccessFunction.update.failed', { name: body.data.name, error: errorMessage }));
    }
  };
}

export function deleteTargetDeliveryTemplateAccessFunction(
  deliveryFunctionId: string,
  id: string,
  body: WithAuditDetails<void>,
  history?: History,
): DeleteTargetDeliveryTemplateAccessFunctionThunkAction {
  return async (dispatch: ThunkDispatch<RootState, unknown, Action<string>>, getState: () => RootState) => {
    const [selectedTargetDeliveryTemplateAccessFunction] = getSelectedTargetDeliveryTemplateAccessFunction(getState());

    try {
      await dispatch({
        type: DELETE_TARGET_DELIVERY_TEMPLATE_ACCESS_FUNCTION,
        payload: api.deleteTargetDeliveryTemplateAccessFunction(deliveryFunctionId, id, body),
      });

      showSuccessMessage(i18n.t('targetDeliveryTemplateAccessFunction.delete.success', { name: selectedTargetDeliveryTemplateAccessFunction?.name }), 5000);
      if (history != null) {
        dispatch(goToTargetDeliveryTemplateAccessFunctionList(history));
      }
    } catch (e) {
      showError(i18n.t('targetDeliveryTemplateAccessFunction.delete.failed', { name: selectedTargetDeliveryTemplateAccessFunction?.name }));

    } finally {
      dispatch(getTargetDeliveryTemplateAccessFunctionList(deliveryFunctionId));
    }
  };
}

export function selectTargetDeliveryTemplateAccessFunction(
  targetDeliveryTemplateAccessFunction: TargetDeliveryTemplateAccessFunction[],
): SelectTargetDeliveryTemplateAccessFunctionAction {
  return {
    type: SELECT_TARGET_DELIVERY_TEMPLATE_ACCESS_FUNCTION,
    payload: targetDeliveryTemplateAccessFunction,
  };
}
